// Load the Jobs to be displayed for the currently selected Dispatch Schedule and date
import { useEffect, useMemo, useState } from 'react';

import { flow, noop, orderBy, unionBy } from 'lodash';

import { firestore } from '@isd/lib/firebase';
import { filterJobsByLocation } from '@isd/lib/firebase/db/helpers';
import {
  DispatchScheduleDoc,
  JobDoc,
  UserDoc,
} from '@isd/lib/firebase/db/metaTypes';
import {
  JOBS_BY_DATE_DEFAULT_SORT_FIELD,
  JOBS_BY_DATE_DEFAULT_SORT_ORDER,
  jobsByDispatchScheduleIdQuery,
  jobsForDispatchScheduleQuery,
} from '@isd/lib/firebase/db/queries';

import { milesToMeters } from '@isd/lib/helpers/maps';
import { getIsJobFiltered } from '@isd/lib/helpers/scheduler';

import { useAuthContext } from '@/context/AuthContext';
import { jobsSorters, useSchedulerContext } from '@/context/SchedulerContext';

/**
 * Apply the parameters of a Dispatch Schedule that can't be expressed in the
 * Firestore query: distance from a center point, sites and vehicle type.
 * @param {JobDoc[]} docs
 * @param {DispatchScheduleDoc | null} dispatchScheduleDoc - Null for "All Jobs".
 * @returns {JobDoc[]}
 */
export function applyDispatchScheduleParams(
  docs: JobDoc[],
  dispatchScheduleDoc: DispatchScheduleDoc | null
): JobDoc[] {
  const { radiusGeoPoint, radiusDistanceMiles, sitesIds } =
    dispatchScheduleDoc?.data() || {};
  let filtered = docs;
  // Check if should filter jobs by distance to center point
  if (!!radiusGeoPoint) {
    filtered = filterJobsByLocation(
      filtered,
      radiusGeoPoint,
      milesToMeters(+(radiusDistanceMiles || 0))
    );
  }
  // Check if should filter jobs by sites
  if (!!sitesIds?.length) {
    filtered = filtered.filter((jobDoc) =>
      jobDoc.get('sitesIds')?.some((jobSiteId) => sitesIds.includes(jobSiteId))
    );
  }
  // Check if should filter jobs by vehicleType
  const vehicleType = dispatchScheduleDoc?.get('vehicleType');
  if (!!vehicleType) {
    filtered = filtered.filter(
      (jobDoc) =>
        jobDoc.get('externalData.commandAlkonOrder.vehicleType.name') ===
        vehicleType
    );
  }
  return filtered;
}

/**
 * Whether a Dispatch Schedule selects jobs by parameters (or is "All Jobs").
 * A schedule without parameters would only show manually assigned jobs.
 * @param {DispatchScheduleDoc | null} dispatchScheduleDoc - Null for "All Jobs".
 * @returns {boolean}
 */
export function getHasDispatchScheduleParams(
  dispatchScheduleDoc: DispatchScheduleDoc | null
): boolean {
  if (!dispatchScheduleDoc) return true;
  return !!(
    dispatchScheduleDoc.get('clientsIds')?.length ||
    dispatchScheduleDoc.get('radiusZipCode') ||
    dispatchScheduleDoc.get('sitesIds')?.length ||
    dispatchScheduleDoc.get('vehicleType')
  );
}

/**
 * Subscribe to the Jobs of the selected Dispatch Schedule for the scheduler's current date,
 * applying the schedule parameters (radius, sites, vehicle type), the selected sort order
 * and the active scheduler filters.
 *
 * Shared by the Jobs list and the Live Map views so both always show the same set of Jobs.
 *
 * @returns {{ jobs: JobDoc[], isLoading: boolean }} The filtered Jobs and a loading flag that
 * stays active until every Job query has returned its first snapshot.
 */
export function useScheduledJobs(): { jobs: JobDoc[]; isLoading: boolean } {
  const [jobs, setJobs] = useState<JobDoc[]>([]);
  const [parametrizedJobs, setParametrizedJobs] = useState<JobDoc[] | null>(
    null
  );
  const [manuallyAssignedJobs, setManuallyAssignedJobs] = useState<
    JobDoc[] | null
  >(null);

  const [isLoading, setIsLoading] = useState(true);
  const { userDoc } = useAuthContext();
  const {
    selectedDispatchSchedule,
    schedulerCurrentDate,
    schedulerSortField,
    schedulerSortOrder,
    initializeSchedulerFilterOptions,
    activeFilters,
    isLoadingDispatchScheduleOptions,
    brokerCompanies,
  } = useSchedulerContext();

  const isUserLoaded = useMemo(() => !!userDoc?.id, [userDoc]);

  // Use a flag to check if should run a query to get All Jobs or
  // Jobs based on selected schedule parameters. If schedule has no
  // parameter set then it should only load manually assigned jobs.
  const shouldLoadParametrizedJobs = useMemo(() => {
    if (!isUserLoaded || isLoadingDispatchScheduleOptions) {
      return false;
    }
    // Load all jobs when no schedule is selected, or the schedule's parametrized jobs
    return getHasDispatchScheduleParams(
      selectedDispatchSchedule.dispatchScheduleDoc
    );
  }, [
    isUserLoaded,
    isLoadingDispatchScheduleOptions,
    selectedDispatchSchedule,
  ]);

  useEffect(() => {
    setIsLoading(true);
    setJobs([]);
    // Clear or reset parametrized jobs state
    if (
      !shouldLoadParametrizedJobs &&
      isUserLoaded &&
      !isLoadingDispatchScheduleOptions
    ) {
      setParametrizedJobs([]);
    } else {
      setParametrizedJobs(null);
    }
    const unSubscribeFromParametrizedJobs = !shouldLoadParametrizedJobs
      ? noop
      : firestore.onSnapshot(
          jobsForDispatchScheduleQuery({
            userDoc: userDoc as UserDoc,
            date: schedulerCurrentDate,
            dispatchScheduleDoc: selectedDispatchSchedule.dispatchScheduleDoc,
          }),
          (querySnapshot) => {
            let docs = firestore.filterSoftDeletedDocs(
              querySnapshot.docs
            ) as JobDoc[];
            docs = applyDispatchScheduleParams(
              docs,
              selectedDispatchSchedule.dispatchScheduleDoc
            );
            setParametrizedJobs(docs);
          }
        );

    const dispatchScheduleId = selectedDispatchSchedule.dispatchScheduleId;
    // NOTE: Manually Assigned Jobs feature (DSP-89) is currently on hold as of 10/21/2024.
    const shouldLoadManuallyAssignedJobs = false; // isUserLoaded && !isLoadingDispatchScheduleOptions && !!dispatchScheduleId;
    // Clear or reset manually assigned jobs state
    if (!shouldLoadManuallyAssignedJobs) {
      setManuallyAssignedJobs([]);
    } else {
      setManuallyAssignedJobs(null);
    }

    const unSubscribeFromManuallyAssignedJobs =
      !shouldLoadManuallyAssignedJobs || !dispatchScheduleId
        ? noop
        : firestore.onSnapshot(
            jobsByDispatchScheduleIdQuery({
              date: schedulerCurrentDate,
              dispatchScheduleId,
            }),
            (querySnapshot) => {
              let docs = firestore.filterSoftDeletedDocs(
                querySnapshot.docs
              ) as JobDoc[];
              setManuallyAssignedJobs(docs);
            }
          );

    return flow([
      unSubscribeFromParametrizedJobs,
      unSubscribeFromManuallyAssignedJobs,
    ]);
  }, [
    isUserLoaded,
    shouldLoadParametrizedJobs,
    selectedDispatchSchedule,
    schedulerCurrentDate,
    isLoadingDispatchScheduleOptions,
  ]);

  useEffect(() => {
    // Merge parametrized and manually selected jobs
    let jobs = unionBy(parametrizedJobs, manuallyAssignedJobs, 'id');

    // Check if should re-order the query result
    if (
      schedulerSortField !== JOBS_BY_DATE_DEFAULT_SORT_FIELD ||
      schedulerSortOrder !== JOBS_BY_DATE_DEFAULT_SORT_ORDER
    ) {
      const jobSorterFn = jobsSorters[schedulerSortField];
      if (!!jobSorterFn) {
        jobs = jobSorterFn(jobs, schedulerSortOrder);
      } else {
        jobs = orderBy(
          jobs,
          (jobDoc) => jobDoc.get(schedulerSortField),
          schedulerSortOrder
        );
      }
    }

    setJobs(jobs);
    // Keep loading flag active until both job queries have been initialized
    const isStillLoading = !manuallyAssignedJobs || !parametrizedJobs;
    // Wait for the jobs before rebuilding the filter options: switching between
    // the List and the Map mounts a fresh copy of this hook, and building them
    // from no jobs would briefly clear the active filters
    if (!isStillLoading) {
      initializeSchedulerFilterOptions(jobs);
    }
    setIsLoading(isStillLoading);
    // A new sort order only re-sorts what's loaded; the queries don't depend on it
  }, [
    parametrizedJobs,
    manuallyAssignedJobs,
    schedulerSortField,
    schedulerSortOrder,
  ]);

  const jobsFiltered = useMemo(() => {
    return jobs.filter((job) =>
      getIsJobFiltered(job, activeFilters, { brokerCompanies })
    );
  }, [jobs, activeFilters, brokerCompanies]);

  return { jobs: jobsFiltered, isLoading };
}
